/**
 * "Your microphone is muted somewhere this app cannot see."
 *
 * The OS mic toggle (macOS Control Center, Windows mic privacy), a headset's
 * hardware mute button, or the OS reclaiming the device: the app still believes
 * the microphone is live — the track is enabled, the producer is resumed, the
 * SFU keeps the peer unmuted — and the person talks to a room that hears
 * nothing. No browser exposes this state directly, and the one hint it does
 * give, `MediaStreamTrack.muted`, fires late and sticks (see useConsumers). So
 * this reads two signals and will not cry wolf on either alone:
 *
 *  - **track.muted / the `mute` event** — a hint, trusted after a short confirm.
 *  - **Sustained *digital* silence** while the app believes the mic is live.
 *
 * The second signal is the subtle one. A live-but-quiet microphone is NOT
 * silent: it sits on a noise floor, usually about -50 to -70 dBFS. A microphone
 * muted by the OS or a hardware switch delivers *true* digital silence — zeros,
 * i.e. -Infinity dBFS. So the silence path only counts levels BELOW a digital-
 * silence line (`digitalSilenceDbfs`, well under any real room's floor); the
 * in-between band — a person who simply is not talking — never trips it. That
 * is what lets this warn on a real mute without nagging whoever is quiet.
 *
 * `speechDbfs` closes the other side: once real audio is reaching us the mic is
 * plainly live, whatever a stale `track.muted` still says, so the warning
 * clears at once.
 *
 * Kept pure — no DOM, no audio, no clock of its own — so the rules are tested
 * directly; `mic-interruption` feeds it a real track's events and energy.
 *
 * Advisory only: nothing here pauses the producer or tells the room. The whole
 * point is that the person believes they are live, so we warn them — we do not
 * change what anyone else sees.
 */

export interface MicSilenceConfig {
  /** A raw mic level above this (dBFS) is somebody talking: definitely live. Clears the warning. */
  speechDbfs: number
  /**
   * At or below this (dBFS) the mic is delivering (near-)true digital silence —
   * a muted or dead device. A live mic's noise floor never sits here, so the
   * band between this and `speechDbfs` (a quiet room) is deliberately ignored.
   */
  digitalSilenceDbfs: number
  /** Continuous digital silence this long (ms) flags a system mute on its own. */
  silentForMs: number
  /** `track.muted` must hold this long (ms) before we trust it — it fires late and sticks. */
  muteConfirmMs: number
  /** Once shown, keep the warning at least this long (ms) so it never just flickers. */
  minVisibleMs: number
}

export const DEFAULT_MIC_SILENCE_CONFIG: MicSilenceConfig = {
  // -50 is what noise-cancellation.ts calls "somebody talking, not a quiet room".
  speechDbfs: -50,
  // Below any real room's noise floor; a muted device reads -Infinity.
  digitalSilenceDbfs: -80,
  silentForMs: 3_000,
  muteConfirmMs: 800,
  minVisibleMs: 1_000,
}

export interface MicSilenceInput {
  /** Monotonic time in ms (performance.now()). */
  now: number
  /** The app believes the mic is live: micOn, producer resumed, track `live`. */
  gated: boolean
  /** The UA's read-only `MediaStreamTrack.muted` — a hint, late and sticky. */
  trackMuted: boolean
  /** Raw mic RMS in dBFS, or null when there is no reliable reading this tick. */
  rmsDbfs: number | null
}

export interface MicSilenceDetector {
  /** Feed one observation; returns whether the system-mute warning should be up. */
  update(input: MicSilenceInput): boolean
  /** Forget all history (e.g. the mic was swapped). */
  reset(): void
  readonly muted: boolean
}

export function createMicSilenceDetector(
  config: MicSilenceConfig = DEFAULT_MIC_SILENCE_CONFIG,
): MicSilenceDetector {
  const { speechDbfs, digitalSilenceDbfs, silentForMs, muteConfirmMs, minVisibleMs } = config

  let silenceSince: number | null = null
  let mutedHintSince: number | null = null
  let reported = false
  let shownAt = 0

  const clearTimers = () => {
    silenceSince = null
    mutedHintSince = null
  }

  const reset = () => {
    clearTimers()
    reported = false
    shownAt = 0
  }

  const update = (i: MicSilenceInput): boolean => {
    // Not live to the app (in-app mute, paused, no live track): not a system
    // mute at all. Clear immediately — no min-visible hold, because this is the
    // user's own choice, not a flap to smooth over.
    if (!i.gated) {
      clearTimers()
      reported = false
      shownAt = 0
      return false
    }

    const level = i.rmsDbfs
    const hasEnergy = level !== null && level > speechDbfs

    let candidate: boolean
    if (hasEnergy) {
      // Real audio is reaching us: the mic is plainly live, whatever a stale
      // track.muted says. This is what clears a sticky mute flag.
      clearTimers()
      candidate = false
    } else {
      // Silence streak counts ONLY true digital silence. A quiet room (the band
      // between digitalSilenceDbfs and speechDbfs) breaks the streak, so a
      // person who is simply not talking never trips the warning.
      const digitalSilence = level !== null && level <= digitalSilenceDbfs
      if (digitalSilence) {
        if (silenceSince === null) silenceSince = i.now
      } else if (level !== null) {
        silenceSince = null
      }
      // level === null (no reliable reading): leave the streak untouched.

      if (i.trackMuted) {
        if (mutedHintSince === null) mutedHintSince = i.now
      } else {
        mutedHintSince = null
      }

      const silenceFlag = silenceSince !== null && i.now - silenceSince >= silentForMs
      const muteFlag = mutedHintSince !== null && i.now - mutedHintSince >= muteConfirmMs
      candidate = silenceFlag || muteFlag
    }

    if (candidate && !reported) {
      reported = true
      shownAt = i.now
    } else if (!candidate && reported && i.now - shownAt >= minVisibleMs) {
      reported = false
    }
    return reported
  }

  return {
    update,
    reset,
    get muted() {
      return reported
    },
  }
}
